export const GENERIC_SYSTEM_ERROR =
  "Ha ocurrido un error inesperado en el sistema. Por favor, intente nuevamente. Si el problema persiste, contacte al administrador.";

export const isEmpty = (v) =>
  v === null || v === undefined || (typeof v === "string" && v.trim() === "");

export const getFirstNonEmpty = (...values) =>
  values.find(v => !isEmpty(v)) ?? null;

export const ok = (data = null) => ({ ok: true, data });
export const fail = (message, error = null) => ({ ok: false, message, error });

/**
 * Formatea un error recibido desde un catch que involucra llamadas axios.
 * 
 * Reglas:
 * 1) Si el error es un string → se retorna tal cual.
 * 2) Si viene de Axios, intenta obtener el mensaje desde las rutas más comunes:
 *    - error.response.data.error.message
 *    - error.response.data.message
 *    - error.response.data (si es texto plain o un objeto simple)
 *    - error.message (mensaje estándar de JS / Axios)
 * 3) Si nada coincide, devuelve un mensaje por defecto.
 */
export function formatAxiosError(error, fallbackMsg = GENERIC_SYSTEM_ERROR) {
  // Caso 1: el error es un string. Ej: throw "Algo falló".
  if (typeof error === "string") {
    return error;
  }

  // Caso 2: Axios envía información dentro de error.response
  const axiosMessage =
    error?.response?.data?.error?.message || // Formato típico de errores API estandarizadas
    error?.response?.data?.message ||        // Mensaje directo desde el backend
    (typeof error?.response?.data === "string"
      ? error.response.data
      : null);                               // Cuando el backend devuelve texto puro

  if (axiosMessage) {
    return axiosMessage;
  }

  // Caso 3: error.message captura errores como:
  // - Timeouts
  // - Network Error
  // - Excepciones del runtime
  if (error?.message) {
    return error.message;
  }

  // Caso 4: fallback final
  return fallbackMsg;
}

export function formatAntdFormError(errorInfo) {
  if (!errorInfo?.errorFields?.length) return null;

  return (
    <div>
      {errorInfo.errorFields.map((field, i) =>
        field.errors.map((err, j) => (
          <div key={`${i}-${j}`}>• {err}</div>
        ))
      )}
    </div>
  );
}

export function showAntdFormError(errorInfo) {
  const message = formatAntdFormError(errorInfo);

  if (!message) return;

  window.messageApi.error(message, { modal: true })
}

/**
 * Convierte un valor a texto legible.
 * @param {*} value Valor a convertir.
 * @param {string} fallback Texto a retornar si el valor no es legible.
 * @returns {string} Texto legible.
 */
export function toReadableText(value, fallback = "") {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (value && typeof value === "object") {
    if (typeof value.message === "string") return value.message;
    if (typeof value.reason === "string") return value.reason;
    try {
      return JSON.stringify(value);
    } catch {
      return fallback;
    }
  }
  return fallback;
}